/**
 * PWAInstallBanner
 * ================
 * Banner não-intrusivo de instalação do PWA.
 *
 * Comportamento:
 * - Aparece após o usuário visitar 2+ páginas
 * - Pode ser dispensado por 7 dias (botão X)
 * - Não aparece se o app já está instalado
 *
 * Estratégia de instalação:
 * - Se o evento `beforeinstallprompt` disparou → usa prompt nativo (1 clique)
 * - Se NÃO disparou (cooldown do Chrome após uninstall, ou iOS Safari) →
 *   redireciona para `/instalar` (página completa com QR + tutorial)
 *
 * Reset automático:
 * - Quando o usuário instala, limpa dismiss key
 * - Quando detecta desinstalação (display-mode volta a browser), limpa dismiss
 */
import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePWAInstall } from '../hooks/usePWAInstall';

const DISMISS_KEY = 'pwa_banner_dismissed_until';
const DISMISS_TS_KEY = 'pwa_banner_dismissed_at';
const PAGE_VIEWS_KEY = 'pwa_page_views';
const LAST_STANDALONE_KEY = 'pwa_was_standalone';
const DISMISS_DAYS = 7;
const MIN_PAGE_VIEWS = 2;

export default function PWAInstallBanner() {
  const navigate = useNavigate();
  const { installPrompt, isInstalled, isIOS, install } = usePWAInstall();
  const [dismissed, setDismissed] = useState(false);
  const [pageViews, setPageViews] = useState(0);

  // Detecta desinstalação para resetar dismiss
  useEffect(() => {
    try {
      const wasStandalone = localStorage.getItem(LAST_STANDALONE_KEY) === '1';
      const isStandaloneNow =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;

      if (isStandaloneNow) {
        localStorage.setItem(LAST_STANDALONE_KEY, '1');
      } else if (wasStandalone) {
        localStorage.removeItem(LAST_STANDALONE_KEY);
        localStorage.removeItem(DISMISS_KEY);
        localStorage.removeItem(DISMISS_TS_KEY);
      }
    } catch (e) {
      // ignore (modo privado)
    }
  }, []);

  useEffect(() => {
    try {
      const current = parseInt(sessionStorage.getItem(PAGE_VIEWS_KEY) || '0', 10);
      const next = current + 1;
      sessionStorage.setItem(PAGE_VIEWS_KEY, String(next));
      setPageViews(next);

      const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (until && Date.now() < until) setDismissed(true);
    } catch (e) {
      // sessionStorage pode falhar em modo privado
    }
  }, []);

  useEffect(() => {
    if (isInstalled) {
      try {
        localStorage.removeItem(DISMISS_KEY);
        localStorage.removeItem(DISMISS_TS_KEY);
      } catch (e) {
        // ignore
      }
    }
  }, [isInstalled]);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(
        DISMISS_KEY,
        String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000),
      );
      localStorage.setItem(DISMISS_TS_KEY, String(Date.now()));
    } catch (e) {
      // ignore
    }
  };

  const handleInstall = useCallback(async () => {
    if (installPrompt) {
      const ok = await install();
      if (ok) handleDismiss();
    } else {
      // Sem prompt nativo → navega pra página completa /instalar
      // (mais consistente que abrir modal sobre o conteúdo atual)
      navigate('/instalar');
    }
  }, [installPrompt, install, navigate]);

  const shouldShow =
    !isInstalled && !dismissed && pageViews >= MIN_PAGE_VIEWS;

  if (!shouldShow) return null;

  const baseCard = {
    position: 'fixed',
    bottom: 24,
    left: 16,
    right: 16,
    maxWidth: 480,
    margin: '0 auto',
    background: 'white',
    borderRadius: 16,
    padding: 16,
    boxShadow: '0 12px 40px rgba(86,0,127,0.25)',
    zIndex: 1000,
    border: '1px solid #E9D5FF',
  };
  const closeBtn = {
    position: 'absolute',
    top: 8,
    right: 8,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
  };

  // iOS Safari — abre /instalar (que tem instruções completas)
  if (isIOS) {
    return (
      <div
        style={baseCard}
        data-testid="pwa-install-banner-ios"
        role="dialog"
        aria-label="Instalar Empreende"
      >
        <button
          type="button"
          aria-label="Fechar"
          onClick={handleDismiss}
          style={closeBtn}
          data-testid="pwa-banner-dismiss"
        >
          <X size={16} color="#666" />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src="/api/favicon"
            alt="Empreende"
            style={{ width: 48, height: 48, borderRadius: 12 }}
          />
          <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
            <p style={{ fontWeight: 700, fontSize: 14, color: '#56007F', margin: 0 }}>
              Instale o Empreende Comunidade
            </p>
            <p style={{ fontSize: 12, color: '#555', margin: '2px 0 0' }}>
              Toque em <Share2 size={12} style={{ verticalAlign: 'middle' }} />{' '}
              abaixo e &ldquo;Adicionar à Tela Inicial&rdquo;
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/instalar')}
            data-testid="pwa-banner-help"
            style={{
              background: '#56007F',
              color: 'white',
              border: 'none',
              borderRadius: 10,
              padding: '8px 12px',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Como?
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={baseCard}
      data-testid="pwa-install-banner"
      role="dialog"
      aria-label="Instalar Empreende"
    >
      <button
        type="button"
        aria-label="Fechar"
        onClick={handleDismiss}
        style={closeBtn}
        data-testid="pwa-banner-dismiss"
      >
        <X size={16} color="#666" />
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <img
          src="/api/favicon"
          alt="Empreende"
          style={{ width: 48, height: 48, borderRadius: 12 }}
        />
        <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
          <p style={{ fontWeight: 700, fontSize: 14, color: '#56007F', margin: 0 }}>
            Instale o Empreende Comunidade
          </p>
          <p style={{ fontSize: 12, color: '#555', margin: '2px 0 0' }}>
            Acesse rápido pela tela inicial e receba avisos
          </p>
        </div>
        <button
          type="button"
          onClick={handleInstall}
          data-testid="pwa-banner-install"
          style={{
            background: '#56007F',
            color: 'white',
            border: 'none',
            borderRadius: 10,
            padding: '8px 14px',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            whiteSpace: 'nowrap',
          }}
        >
          <Download size={14} />
          Instalar
        </button>
      </div>
    </div>
  );
}
